/**
 * Player - FC-style footballer with dribbling, through ball, lob, and tackle.
 * WASD move | Shift sprint | Space shoot | E through ball | Q lob | F tackle
 * 
 * TACKLE SYSTEM: Player must use F (tackle) to take ball from opponent.
 * Cannot just walk into ball to take it - must tackle!
 */

import * as BABYLON from "babylonjs";
import { InputSystem } from "../systems/InputSystem";
import { Ball } from "./Ball";
import { loadFBXCharacter, HumanoidAnimator, KickType, CelebrationType } from "./HumanoidMesh";

const MOVE_SPEED = 6.05;        // ~22 km/h jog (10% faster than AI's 5.5)
const SPRINT_SPEED = 9.68;      // ~35 km/h sprint (10% faster than AI's 8.8)
const STAMINA_MAX = 150;        // Increased from 100
const STAMINA_DRAIN = 15;       // Slower drain (was 25)
const STAMINA_REGEN = 30;       // Faster recovery (was 20)

// Kick (realistic ball physics)
const KICK_RANGE = 2.0;       // closer range for better control
const KICK_ASSIST_RANGE = 3.5;  // slightly smaller
const KICK_POWER_MIN = 3;     // minimum tap pass
const KICK_POWER_MAX = 12;    // powerful shot
const KICK_CHARGE_RATE = 4;   // slower charge for more control
const KICK_COOLDOWN = 0.4;    // slight delay between kicks

// Through ball (E)
const THROUGH_POWER = 8;      // faster ground pass
const THROUGH_RANGE = 3;

// Lob / chip (Q)
const LOB_POWER = 7;
const LOB_ARC = 0.6;          // lower arc for more realistic chip
const LOB_RANGE = 3;

// Slide tackle (F) - REQUIRED to take ball from opponent
const TACKLE_SPEED = 14;
const TACKLE_DURATION = 0.4;
const TACKLE_COOLDOWN = 1.2;
const TACKLE_RANGE = 3;
const TACKLE_BALL_STEAL_RANGE = 2.5; // Range to steal ball with tackle

// Dribbling (realistic - natural ball control)
// Player can ONLY dribble if they have ball possession
const DRIBBLE_RADIUS = 2.0;           
const DRIBBLE_CLOSE_RANGE = 1.2;      
const DRIBBLE_NUDGE_FORCE = 60;       
const DRIBBLE_CLOSE_FORCE = 120;       
const DRIBBLE_MAX_SPEED = 18;         
const DRIBBLE_TOUCH_DISTANCE = 0.85;  // increased from 0.75
const LOSE_BALL_SPEED = 20;           

// Ball possession system
const POSSESSION_RANGE = 1.5; // Must be this close to gain/keep possession
const POSSESSION_LOSE_RANGE = 3.0; // Lose possession if ball gets this far away

// Movement physics (realistic human movement)
const ACCELERATION = 55;       // Much snappier
const DECELERATION = 65;       
const TURN_ACCEL = 45;         // Faster response when turning
const TURN_SPEED_LOW = 12;      // Faster rotation
const TURN_SPEED_HIGH = 7;     // Faster rotation at high speed

export class Player {
  public mesh: BABYLON.Mesh;
  public stamina = STAMINA_MAX;
  public kickCharge = 0;
  public isTackling = false;
  public hasBallPossession = false; // NEW: Track ball possession
  public animator: HumanoidAnimator;

  private _input: InputSystem;
  private _ball: Ball;
  private _camera: BABYLON.FollowCamera;
  private _collider: BABYLON.Mesh;
  private _wasShootDown = false;
  private _kickCooldown = 0;
  private _currentSpeed = 0;
  private _velocity = BABYLON.Vector3.Zero();
  private _targetAngle = 0;
  private _turnRate = 0;

  // Tackle state
  private _tackleTimer = 0;
  private _tackleCooldown = 0;
  private _tackleDir = BABYLON.Vector3.Zero();

  // Dribble cooldown after kick
  private _dribbleCooldown = 0;

  // Through / lob one-shot triggers
  private _wasThroughDown = false;
  private _wasLobDown = false;

  // Celebration state
  private _celebrationTarget: BABYLON.Vector3 | null = null;
  private _isAutoRunning = false;
  private _celebrationSkipPressed = false;
  private _celebrationInputCooldown = 0;

  constructor(
    scene: BABYLON.Scene,
    input: InputSystem,
    ball: Ball,
    camera: BABYLON.FollowCamera,
    shadowGenerator?: BABYLON.ShadowGenerator
  ) {
    this._input = input;
    this._ball = ball;
    this._camera = camera;

    // Create placeholder root immediately for positioning
    this.mesh = BABYLON.MeshBuilder.CreateBox("player_placeholder", { size: 0.5 }, scene);
    this.mesh.isVisible = false;
    const placeholderMat = new BABYLON.StandardMaterial("player_placeholder_mat", scene);
    placeholderMat.diffuseColor = new BABYLON.Color3(1, 0, 0);
    this.mesh.material = placeholderMat;
    this.mesh.position = new BABYLON.Vector3(0, 0, -10);

    // Dummy animator until FBX loads
    this.animator = new HumanoidAnimator(null as any);

    // Load user's Blender model with enhanced PBR materials and correct colors
    loadFBXCharacter("player", scene, {
        skin: new BABYLON.Color3(1.0, 0.95, 0.9),
        jersey: new BABYLON.Color3(0.9, 0.05, 0.05),
        shorts: new BABYLON.Color3(0.05, 0.7, 0.05),
    }, shadowGenerator).then(({ root, animations }) => {
      root.position.copyFrom(this.mesh.position);
      root.rotation.copyFrom(this.mesh.rotation);
      this.mesh.dispose();
      this.mesh = root;
      this.animator.setFBXAnimations(animations);
      console.log("Player Blender model loaded with PBR and custom colors!");
    });

    this._collider = BABYLON.MeshBuilder.CreateCylinder(
      "playerCollider",
      { height: 1.6, diameter: 0.5, tessellation: 10 },
      scene
    );
    this._collider.isVisible = false;
    this._collider.physicsImpostor = new BABYLON.PhysicsImpostor(
      this._collider,
      BABYLON.PhysicsImpostor.CylinderImpostor,
      { mass: 0, restitution: 0.3, friction: 0.2 },
      scene
    );
  }

  update(dt: number): void {
    // Update dribble cooldown
    if (this._dribbleCooldown > 0) {
      this._dribbleCooldown -= dt;
    }

    // Update celebration input cooldown
    if (this._celebrationInputCooldown > 0) {
      this._celebrationInputCooldown -= dt;
    }

    // Check for input to trigger or skip celebration
    if (this._isAutoRunning || this.animator.isCelebrating) {
      const hasInput = this._input.forward || this._input.backward || this._input.left || this._input.right || this._input.sprint || this._input.shoot;
      
      if (hasInput) {
        if (this._isAutoRunning) {
          this._isAutoRunning = false;
          this._celebrationTarget = null;
          this._currentSpeed = 0;
          this.animator.triggerCelebration(CelebrationType.Dance);
          this._celebrationInputCooldown = 1.0;
        } else if (this.animator.isCelebrating && !this._celebrationSkipPressed && this._celebrationInputCooldown <= 0) {
          this._celebrationSkipPressed = true;
          this.animator._skipCelebration();
        }
      }
    }

    if (this._tackleTimer > 0) {
      this._updateTackle(dt);
    } else if (this._isAutoRunning) {
      this._handleAutoRun(dt);
    } else if (!this.animator.isCelebrating) {
      this._handleMovement(dt);
      // Only dribble if player has ball possession
      if (this.hasBallPossession) {
        this._handleDribble();
      }
      // Check if possession should be lost or gained
      this._updatePossession();
    }
    this._handleStamina(dt);
    this._handleKick(dt);
    this._handleThroughBall();
    this._handleLob();
    this._handleTackleInput(dt);
    this.animator.animate(dt, this._currentSpeed, this.animator.isKicking, this._turnRate);
    this._syncCollider();
  }

  private _syncCollider(): void {
    this._collider.position.set(this.mesh.position.x, 0.8, this.mesh.position.z);
  }

  // ─── Ball Possession System ──────────────────────────────────────

  private _updatePossession(): void {
    const ballPos = this._ball.mesh.position;
    const myPos = this.mesh.position;
    const dist = BABYLON.Vector3.Distance(ballPos, myPos);

    // Lose possession if ball gets too far away
    if (this.hasBallPossession && dist > POSSESSION_LOSE_RANGE) {
      this.hasBallPossession = false;
    }
    
    // Gain possession when close to ball (for natural dribbling)
    // This allows the player to dribble without needing to tackle first
    if (!this.hasBallPossession && dist < POSSESSION_RANGE) {
      this.hasBallPossession = true;
    }
  }

  // Called by AI when they tackle the player
  public losePossession(): void {
    this.hasBallPossession = false;
  }

  // ─── Movement ───────────────────────────────────────────────────

  private _handleMovement(dt: number): void {
    const raw = new BABYLON.Vector3(0, 0, 0);
    if (this._input.forward) raw.z += 1;
    if (this._input.backward) raw.z -= 1;
    if (this._input.left) raw.x -= 1;
    if (this._input.right) raw.x += 1;

    if (raw.length() === 0) {
      this._velocity.scaleInPlace(Math.max(0, 1 - DECELERATION * dt));
    } else {
      raw.normalize();

      const cameraForward = this._camera.getDirection(BABYLON.Vector3.Forward());
      cameraForward.y = 0;
      cameraForward.normalize();
      const cameraRight = this._camera.getDirection(BABYLON.Vector3.Right());
      cameraRight.y = 0;
      cameraRight.normalize();

      const targetDir = cameraForward.scale(raw.z).add(cameraRight.scale(raw.x));
      targetDir.normalize();

      const isSprinting = this._input.sprint && this.stamina > 0;
      const targetSpeed = isSprinting ? SPRINT_SPEED : MOVE_SPEED;

      let accel = ACCELERATION;
      const curLen = this._velocity.length();
      if (curLen > 0.5) {
        const curDir = this._velocity.normalizeToNew();
        const directionChange = BABYLON.Vector3.Dot(curDir, targetDir);
        if (directionChange < 0.2) {
          accel = TURN_ACCEL;
        }
      }

      const target = targetDir.scale(targetSpeed);
      const lerpFactor = Math.min(1, accel * dt / Math.max(targetSpeed, 1));
      this._velocity = BABYLON.Vector3.Lerp(this._velocity, target, lerpFactor);
    }

    this.mesh.position.addInPlace(this._velocity.scale(dt));
    this.mesh.position.x = BABYLON.Scalar.Clamp(this.mesh.position.x, -18.8, 18.8);
    this.mesh.position.z = BABYLON.Scalar.Clamp(this.mesh.position.z, -28.8, 28.8);

    this._currentSpeed = this._velocity.length();

    if (this._currentSpeed > 0.3) {
      this._targetAngle = Math.atan2(this._velocity.x, this._velocity.z);
      if (isNaN(this._targetAngle)) this._targetAngle = 0;

      const turnSpeed = BABYLON.Scalar.Lerp(TURN_SPEED_LOW, TURN_SPEED_HIGH, this._currentSpeed / SPRINT_SPEED);

      let diff = this._targetAngle - this.mesh.rotation.y;
      if (isNaN(diff)) diff = 0;

      while (diff > Math.PI) diff -= 2 * Math.PI;
      while (diff < -Math.PI) diff += 2 * Math.PI;

      this._turnRate = diff / Math.max(dt, 0.001);

      const step = turnSpeed * dt;
      if (Math.abs(diff) < step) {
        this.mesh.rotation.y = this._targetAngle;
      } else {
        this.mesh.rotation.y += step * Math.sign(diff);
      }
    } else {
      this._turnRate = 0;
    }
  }

  // ─── Auto-dribble ───────────────────────────────────────────────

  private _handleDribble(): void {
    if (this._dribbleCooldown > 0) return;

    const ballPos = this._ball.mesh.position;
    const myPos = this.mesh.position;
    const dx = ballPos.x - myPos.x;
    const dz = ballPos.z - myPos.z;
    const distSq = dx * dx + dz * dz;
    
    if (distSq > DRIBBLE_RADIUS * DRIBBLE_RADIUS) return;

    this._ball.registerTouch();

    const imp = this._ball.mesh.physicsImpostor;
    if (!imp) return;

    const dist = Math.sqrt(distSq);
    const facingX = Math.sin(this.mesh.rotation.y);
    const facingZ = Math.cos(this.mesh.rotation.y);

    const toBallX = dx / dist;
    const toBallZ = dz / dist;
    const dot = facingX * toBallX + facingZ * toBallZ;

    const isTurning = Math.abs(this._turnRate) > 0.5;
    const coneThreshold = isTurning ? -0.9 : -0.6; 
    if (dot < coneThreshold && dist > DRIBBLE_CLOSE_RANGE) return;

    if (this._currentSpeed > LOSE_BALL_SPEED && this._input.sprint) return; 

    let targetPosX: number;
    let targetPosZ: number;

    if (dot > 0) {
        targetPosX = myPos.x + facingX * DRIBBLE_TOUCH_DISTANCE;
        targetPosZ = myPos.z + facingZ * DRIBBLE_TOUCH_DISTANCE;
    } else {
        targetPosX = myPos.x + toBallX * DRIBBLE_TOUCH_DISTANCE;
        targetPosZ = myPos.z + toBallZ * DRIBBLE_TOUCH_DISTANCE;
    }

    if (dist < DRIBBLE_CLOSE_RANGE) {
        if (dist < 0.55) {
            const snapX = myPos.x + facingX * 0.6;
            const snapZ = myPos.z + facingZ * 0.6;
            ballPos.x = snapX;
            ballPos.z = snapZ;
        }

        const lockedVelX = this._velocity.x + (targetPosX - ballPos.x) * 15;
        const lockedVelZ = this._velocity.z + (targetPosZ - ballPos.z) * 15;
        
        const currentVel = imp.getLinearVelocity()!;
        imp.setLinearVelocity(new BABYLON.Vector3(lockedVelX, currentVel.y, lockedVelZ));
        
        const spinSpeed = this._currentSpeed / 0.22; 
        imp.setAngularVelocity(new BABYLON.Vector3(lockedVelZ * spinSpeed / 15, 0, -lockedVelX * spinSpeed / 15));
    } else {
        const forceX = (targetPosX - ballPos.x) * DRIBBLE_NUDGE_FORCE * 8;
        const forceZ = (targetPosZ - ballPos.z) * DRIBBLE_NUDGE_FORCE * 8;
        imp.applyForce(new BABYLON.Vector3(forceX, 0, forceZ), ballPos);
    }

    if (ballPos.y > 0.25) {
      imp.applyForce(new BABYLON.Vector3(0, -40, 0), ballPos);
    }
  }

  // ─── Stamina ────────────────────────────────────────────────────

  private _handleStamina(dt: number): void {
    const draining = this._input.sprint && this.stamina > 0;
    if (draining || this._tackleTimer > 0) {
      this.stamina -= STAMINA_DRAIN * dt;
      if (this.stamina < 0) this.stamina = 0;
    } else {
      this.stamina += STAMINA_REGEN * dt;
      if (this.stamina > STAMINA_MAX) this.stamina = STAMINA_MAX;
    }
  }

  // ─── Aim assist ─────────────────────────────────────────────────

  private _aimAssist(range: number): boolean {
    const toBall = this._ball.mesh.position.subtract(this.mesh.position);
    toBall.y = 0;
    const dist = toBall.length();

    if (dist > range) return false;

    if (dist > KICK_RANGE * 0.8) {
      const lungeDir = toBall.normalize();
      const lungeAmount = Math.min(dist - KICK_RANGE * 0.5, 1.5);
      this.mesh.position.addInPlace(lungeDir.scale(lungeAmount));
    }

    const angle = Math.atan2(toBall.x, toBall.z);
    this.mesh.rotation.y = angle;

    return true;
  }

  // ─── Standard kick ───────────────────────────────────────────────

  private _handleKick(dt: number): void {
    if (this._kickCooldown > 0) this._kickCooldown -= dt;
    const shootDown = this._input.shoot;

    if (shootDown) {
      this.kickCharge = Math.min(1, this.kickCharge + KICK_CHARGE_RATE * dt);
    } else if (this._wasShootDown && this._kickCooldown <= 0) {
      const type = this.kickCharge > 0.7 ? KickType.Power : KickType.Normal;
      this.animator.triggerKick(type);
      this._kickCooldown = KICK_COOLDOWN;

      const toBall = this._ball.mesh.position.subtract(this.mesh.position);
      toBall.y = 0;
      const dist = toBall.length();

      const canKick = dist <= KICK_ASSIST_RANGE || dist <= DRIBBLE_CLOSE_RANGE;

      if (canKick) {
        const power = KICK_POWER_MIN + this.kickCharge * (KICK_POWER_MAX - KICK_POWER_MIN);

        if (dist > DRIBBLE_CLOSE_RANGE) {
          this._aimAssist(KICK_ASSIST_RANGE);
        }

        let kickDir: BABYLON.Vector3;
        if (this.kickCharge >= 0.9) {
          kickDir = new BABYLON.Vector3(0, 0.35, 1);
        } else {
          kickDir = new BABYLON.Vector3(
            Math.sin(this.mesh.rotation.y), 0.25, Math.cos(this.mesh.rotation.y)
          );
        }

        // Calculate automatic spin
        const forward = kickDir.normalizeToNew();
        const right = BABYLON.Vector3.Cross(forward, BABYLON.Vector3.Up());
        
        // 1. Side-spin from lateral movement (swerve)
        const lateralSpeed = BABYLON.Vector3.Dot(this._velocity, right);
        const sideSpin = BABYLON.Vector3.Up().scale(-lateralSpeed * 0.8);
        
        // 2. Top-spin for Power shots (dip)
        let topSpin = BABYLON.Vector3.Zero();
        if (type === KickType.Power) {
          topSpin = right.scale(3.0); // Forward spin axis is 'right' for top-spin
        }
        
        const finalSpin = sideSpin.add(topSpin);

        this._ball.kick(kickDir, power, this._velocity, finalSpin);
        this._dribbleCooldown = 0.5;
        // Lose possession after kicking
        this.hasBallPossession = false;
      }
      this.kickCharge = 0;
    }
    this._wasShootDown = shootDown;
  }

  // ─── Through ball ───────────────────────────────────────────────

  private _handleThroughBall(): void {
    const down = this._input.throughPass;
    if (down && !this._wasThroughDown) {
      this.animator.triggerKick(KickType.Normal);
      if (this._aimAssist(THROUGH_RANGE)) {
        const facing = new BABYLON.Vector3(
          Math.sin(this.mesh.rotation.y), 0.05, Math.cos(this.mesh.rotation.y)
        );
        this._ball.kick(facing, THROUGH_POWER, this._velocity);
        this._dribbleCooldown = 0.5;
        this.hasBallPossession = false;
      }
    }
    this._wasThroughDown = down;
  }

  // ─── Lob ───────────────────────────────────────────────────────

  private _handleLob(): void {
    const down = this._input.lobKick;
    if (down && !this._wasLobDown) {
      this.animator.triggerKick(KickType.Chip);
      if (this._aimAssist(LOB_RANGE)) {
        const facing = new BABYLON.Vector3(
          Math.sin(this.mesh.rotation.y), LOB_ARC, Math.cos(this.mesh.rotation.y)
        );
        // Add backspin for lob: rotate around axis perpendicular to direction
        const spinAxis = BABYLON.Vector3.Cross(facing, BABYLON.Vector3.Up()).normalize();
        const backspin = spinAxis.scale(-2.0); // Constant backspin for chips
        
        this._ball.kick(facing, LOB_POWER, this._velocity, backspin);
        this._dribbleCooldown = 0.5;
        this.hasBallPossession = false;
      }
    }
    this._wasLobDown = down;
  }

  // ─── Slide tackle (F) - REQUIRED to steal ball ───────────────────

  private _handleTackleInput(dt: number): void {
    if (this._tackleCooldown > 0) this._tackleCooldown -= dt;

    if (this._input.tackle && this._tackleTimer <= 0 && this._tackleCooldown <= 0 && this.stamina > 10) {
      this._tackleTimer = TACKLE_DURATION;
      this._tackleCooldown = TACKLE_COOLDOWN;
      this.isTackling = true;

      this._tackleDir = new BABYLON.Vector3(
        Math.sin(this.mesh.rotation.y), 0, Math.cos(this.mesh.rotation.y)
      );
    }
  }

  private _updateTackle(dt: number): void {
    this._tackleTimer -= dt;
    this._currentSpeed = TACKLE_SPEED;

    this.mesh.position.addInPlace(this._tackleDir.scale(TACKLE_SPEED * dt));
    this.mesh.position.x = BABYLON.Scalar.Clamp(this.mesh.position.x, -19.7, 19.7);
    this.mesh.position.z = BABYLON.Scalar.Clamp(this.mesh.position.z, -29.7, 29.7);

    // Check for ball steal during tackle
    const toBall = this._ball.mesh.position.subtract(this.mesh.position);
    toBall.y = 0;
    const dist = toBall.length();
    
    if (dist < TACKLE_BALL_STEAL_RANGE) {
      // Steal the ball! Knock it in tackle direction and gain possession
      this._ball.kick(this._tackleDir.add(new BABYLON.Vector3(0, 0.1, 0)), 4);
      this.hasBallPossession = true;
    }

    if (this._tackleTimer <= 0) {
      this.isTackling = false;
    }
  }

  resetPosition(): void {
    this.mesh.position = new BABYLON.Vector3(0, 0, -10);
    this.mesh.rotation.y = 0;
    this.stamina = STAMINA_MAX;
    this.isTackling = false;
    this.hasBallPossession = false;
    this._tackleTimer = 0;
    this._velocity = BABYLON.Vector3.Zero();
    this._dribbleCooldown = 0;
    this._isAutoRunning = false;
    this._celebrationTarget = null;
    this._celebrationSkipPressed = false;
    this._celebrationInputCooldown = 0;
    this.animator._skipCelebration();
  }

  celebrateAt(targetPos: BABYLON.Vector3): void {
    this._celebrationTarget = targetPos;
    this._isAutoRunning = true;
  }

  private _handleAutoRun(dt: number): void {
    if (!this._celebrationTarget) return;

    const toTarget = this._celebrationTarget.subtract(this.mesh.position);
    toTarget.y = 0;
    const dist = toTarget.length();

    if (dist > 1.0) {
      const dir = toTarget.normalize();
      this.mesh.position.addInPlace(dir.scale(MOVE_SPEED * dt));
      this._currentSpeed = MOVE_SPEED;
      
      this._targetAngle = Math.atan2(dir.x, dir.z);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, this._targetAngle, 0.1);
    } else {
      this._isAutoRunning = false;
      this._celebrationTarget = null;
      this._currentSpeed = 0;
      this.animator.triggerCelebration(CelebrationType.Dance);
      this._celebrationInputCooldown = 1.0;
    }
  }
}
